import { useCallback, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates as unstableBatchedUpdates } from 'react-dom';
import { useQueryClient } from 'react-query';

import postPriceAndStock from '@/common/api/products/postPriceAndStock';
import { ENDPOINT_PRICE_AND_STOCK } from '@/common/constants';
import {
  MERCURE_MESSAGE_TYPE_PRICE_AND_STOCK,
  useMercureContext,
} from '@/common/contexts/MercureContext/MercureContext';
import useMercureMessageSubscriber from '@/common/contexts/MercureContext/useMercureMessageSubscriber';

const MAX_RETRY = 3;
const FETCH_DELAY = 500;
const RESPONSES_CONSUME_DELAY = 600;

const getDataSignature = (data, productsMapper) => {
  const skus = [];
  productsMapper(data, (product) => {
    const hasPrice = product?.price_and_stock?.has_price ?? false;
    const hasStock = product?.price_and_stock?.has_stock ?? false;
    skus.push(`${product.sku}-${hasPrice}-${hasStock}`);
  });

  if (skus.length === 0) return null;

  return skus.sort().toString();
};

const priceAndStockFormatter = (priceAndStock) => {
  if (priceAndStock === undefined) return undefined;

  return {
    ...priceAndStock,
    split_packaging: priceAndStock.split_packaging ?? false,
    sales_packaging: priceAndStock.sales_packaging ?? 1,
  };
};

const usePricesAndStocks = (
  queryKey,
  productsMapper,
  productsMutator,
  isActive = false,
  withMercure = false,
) => {
  const queryClient = useQueryClient();
  const { isConnectedPrivate, userTopic } = useMercureContext();
  const isMercureAvailable = isConnectedPrivate && withMercure;
  const prevDataSignatureRef = useRef(null);
  const productsRef = useRef(new Map());
  const responseRef = useRef(new Map());
  const responseConsumerIntervalRef = useRef(null);
  const data = queryClient.getQueryData(queryKey);

  const [total, setTotal] = useState(0);
  const [totalWithPrice, setTotalWithPrice] = useState(0);
  const [totalWithStock, setTotalWithStock] = useState(0);
  const [totalPending, setTotalPending] = useState(0);
  const [totalFulfilled, setTotalFulfilled] = useState(0);

  /*
   * Browse products to update all states
   */
  const updateStates = useCallback(() => {
    let nextTotal = 0;
    let nextTotalWithPrice = 0;
    let nextTotalWithStock = 0;
    let nextTotalPending = 0;
    let nextTotalFulfilled = 0;
    productsRef.current.forEach((product) => {
      nextTotal += 1;
      nextTotalWithPrice += product.hasPrice ? 1 : 0;
      nextTotalWithStock += product.hasStock ? 1 : 0;
      nextTotalPending += product.pending ? 1 : 0;
      nextTotalFulfilled += product.fulfilled ? 1 : 0;
    });
    // In React 17 or Earlier Versions there is no automatics batch Updates in Timeout or promise function
    // TODO : delete unstable_batchedUpdates with React 18+ upgrade
    unstableBatchedUpdates(() => {
      setTotal(nextTotal);
      setTotalWithPrice(nextTotalWithPrice);
      setTotalWithStock(nextTotalWithStock);
      setTotalPending(nextTotalPending);
      setTotalFulfilled(nextTotalFulfilled);
    });
  }, []);

  /*
   * Empty all products and stop fetch timeout
   */
  const clearProducts = useCallback((products) => {
    products.forEach((product, sku) => {
      if (product.timeoutId) {
        clearTimeout(product.timeoutId);
      }
      productsRef.current.delete(sku);
    });
  }, []);

  /*
   * Get priceAndStock infos and fetch tries count
   */
  const getPricesAndStocksStatus = useCallback((product, priceAndStock) => {
    const hasPrice = priceAndStock?.has_price ?? false;
    const hasStock = priceAndStock?.has_stock ?? false;
    const hasPriceAndStock = hasPrice && hasStock;
    const retry = product?.retry ?? 0;
    const fulfilled = hasPriceAndStock || retry === MAX_RETRY;

    return {
      hasPrice,
      hasStock,
      retry,
      fulfilled,
    };
  }, []);

  /*
   * Toggle fetch timout
   */
  const toggleFetcher = useCallback(
    (sku, product, priceAndStock, fetcher, responseConsumer) => {
      const { fulfilled, retry } = getPricesAndStocksStatus(
        product,
        priceAndStock,
      );
      const delay = retry === 0 ? FETCH_DELAY : 2000 * 2 ** retry;
      const pending = !fulfilled;
      let timeoutId = null;
      if (!fulfilled && !isMercureAvailable) {
        if (!product?.timeoutId) {
          timeoutId = setTimeout(() => fetcher(sku, responseConsumer), delay);
        } else {
          timeoutId = product.timeoutId;
        }
      } else if (product?.timeoutId) {
        clearTimeout(product.timeoutId);
      }

      return { pending, timeoutId };
    },
    [getPricesAndStocksStatus, isMercureAvailable],
  );

  /*
   * Start or stop responses consumer
   */
  const toggleResponsesConsumer = useCallback((responseConsumer) => {
    let isComplete = true;
    productsRef.current.forEach((product) => {
      if (!product.fulfilled) {
        isComplete = false;
      }
    }, []);
    if (!isComplete) {
      if (responseConsumerIntervalRef.current === null) {
        responseConsumerIntervalRef.current = setInterval(
          responseConsumer,
          RESPONSES_CONSUME_DELAY,
        );
      }
    } else {
      clearInterval(responseConsumerIntervalRef.current);
      responseConsumerIntervalRef.current = null;
    }
  }, []);

  /*
   * Mutate original data with prices and stocks
   */
  const mutate = useCallback(
    (mutationPriceAndStock) => {
      const prevData = queryClient.getQueryData(queryKey);
      if (mutationPriceAndStock.length === 0 || !prevData) return;

      const nextData = productsMutator(prevData, (product) => {
        const priceAndStock = mutationPriceAndStock.get(product.sku);
        if (!priceAndStock) {
          return product;
        }
        return {
          ...product,
          price_and_stock: priceAndStockFormatter(priceAndStock),
        };
      });
      queryClient.setQueryData(queryKey, nextData);
    },
    [productsMutator, queryClient, queryKey],
  );

  /*
   * Call by setInterval, update products status
   */
  const consumeResponses = useCallback(() => {
    const pricesAndStocks = new Map();
    const nextProducts = new Map(productsRef.current);
    productsRef.current.forEach((product, sku) => {
      const priceAndStock = responseRef.current.get(sku);
      const nextProduct = { ...product };
      if (priceAndStock) {
        const { hasPrice, hasStock, fulfilled } = getPricesAndStocksStatus(
          product,
          priceAndStock,
        );
        nextProduct.hasPrice = hasPrice;
        nextProduct.hasStock = hasStock;
        nextProduct.fulfilled = fulfilled;
        if (
          (hasPrice && !product.hasPrice) ||
          (hasStock && !product.hasStock)
        ) {
          pricesAndStocks.set(sku, priceAndStock);
        }
      } else {
        nextProduct.fulfilled =
          (product.hasPrice && product.hasStock) || product.retry === MAX_RETRY;
      }
      nextProducts.set(sku, nextProduct);
    });
    responseRef.current = new Map();
    productsRef.current = nextProducts;
    if (pricesAndStocks.size > 0) {
      mutate(pricesAndStocks);
    }
    updateStates();
    toggleResponsesConsumer(consumeResponses);
  }, [getPricesAndStocksStatus, mutate, toggleResponsesConsumer, updateStates]);

  /*
   * Fetch price and stock response handler
   */
  const handleFetchResponse = useCallback(
    (sku, priceAndStock, retry, fetcher, responseConsumer) => {
      const currentProduct = productsRef.current.get(sku);
      if (currentProduct) {
        const nextProduct = {
          ...currentProduct,
          retry,
          timeoutId: null,
        };
        const { timeoutId, pending } = toggleFetcher(
          sku,
          nextProduct,
          priceAndStock,
          fetcher,
          responseConsumer,
        );
        nextProduct.timeoutId = timeoutId;
        nextProduct.pending = pending;

        responseRef.current.set(sku, priceAndStock);
        productsRef.current.set(sku, nextProduct);
      }
    },
    [toggleFetcher],
  );

  /*
   * Fetch price and stock
   */
  const fetch = useCallback(
    (sku, responseConsumer) => {
      const currentProduct = productsRef.current.get(sku);
      if (currentProduct) {
        const { fulfilled } = currentProduct;
        if (!fulfilled) {
          postPriceAndStock({ queryKey: [ENDPOINT_PRICE_AND_STOCK, sku] })
            .then((response) => {
              handleFetchResponse(
                sku,
                response.data,
                currentProduct.retry + 1,
                fetch,
                responseConsumer,
              );
            })
            .catch(() => {
              handleFetchResponse(
                sku,
                null,
                currentProduct.retry + 1,
                fetch,
                responseConsumer,
              );
            });
        }
      }
    },
    [handleFetchResponse],
  );

  useEffect(() => {
    if (data && isActive) {
      const dataSignature = getDataSignature(data, productsMapper);
      if (dataSignature !== prevDataSignatureRef.current) {
        prevDataSignatureRef.current = dataSignature;
        const productsToClear = new Map(productsRef.current);
        productsMapper(data, (dataProduct) => {
          const { sku, price_and_stock: priceAndStock } = dataProduct;
          const prevProduct = productsRef.current.get(sku);
          const {
            hasPrice,
            hasStock,
            retry,
            fulfilled,
          } = getPricesAndStocksStatus(prevProduct, priceAndStock);
          const { timeoutId, pending } = toggleFetcher(
            sku,
            prevProduct,
            priceAndStock,
            fetch,
            consumeResponses,
          );
          productsRef.current.set(sku, {
            sku,
            hasPrice,
            hasStock,
            pending,
            fulfilled,
            retry,
            timeoutId,
          });
          if (prevProduct) {
            productsToClear.delete(sku);
          }
        });
        clearProducts(productsToClear);
        updateStates();
        toggleResponsesConsumer(consumeResponses);
      }
    }
  }, [
    clearProducts,
    data,
    fetch,
    isActive,
    productsMapper,
    toggleResponsesConsumer,
    consumeResponses,
    toggleFetcher,
    getPricesAndStocksStatus,
    updateStates,
  ]);

  useEffect(() => {
    if (isMercureAvailable) {
      toggleResponsesConsumer(consumeResponses);
    }
  }, [consumeResponses, isMercureAvailable, toggleResponsesConsumer]);

  useEffect(
    () => () => {
      productsRef.current.forEach((product) => {
        clearTimeout(product.timeout);
      });
      productsRef.current.clear();
      clearInterval(responseConsumerIntervalRef.current);
      responseConsumerIntervalRef.current = null;
    },
    [],
  );

  useMercureMessageSubscriber(
    userTopic,
    MERCURE_MESSAGE_TYPE_PRICE_AND_STOCK,
    (message) => {
      const priceAndStock = message.payload;
      if (priceAndStock) {
        handleFetchResponse(
          priceAndStock.sku,
          priceAndStock,
          1,
          fetch,
          consumeResponses,
        );
      }
    },
  );

  return {
    total,
    totalWithPrice,
    totalWithStock,
    totalPending,
    totalFulfilled,
  };
};

export default usePricesAndStocks;
